import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useDispatch } from "react-redux";
import { getForecastHistoryFilterData } from "../../redux/actions/getForecastHistoryFilterDataSlice";
import {
  getBuyerExtractionCalendarAccessibleRegionsEndpoint,
  postBuyerExtractionCalendarEndpoint,
} from "../../common/endpoints";
import { Loader } from "../../components/common/Loader";

const monthName = (monthIndex) =>
  new Date(2000, monthIndex, 1).toLocaleString("en-US", { month: "long" });

const EMPTY_RESPONSE = {
  filters: {
    year: new Date().getFullYear(),
    month: monthName(new Date().getMonth()),
    regions: [],
  },
  summary: {
    totalPlants: 0,
    fullyExecuted: 0,
    regionCounts: {},
  },
  data: [],
};

const ALL_MONTHS = Array.from({ length: 12 }, (_, i) => monthName(i));
const YEAR_RANGE_COUNT = 5;
const DEFAULT_REGION_OPTION = "ALL";

const normalizeRegionLabel = (value) => {
  if (!value || typeof value !== "string") return "";
  const normalized = value.trim().toUpperCase();
  if (normalized === "AMERICAS") return "AMERICS";
  return normalized;
};

const parseYearMonthFromDate = (dateValue) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return { year: null, month: null };
  }
  return {
    year: date.getUTCFullYear(),
    month: monthName(date.getUTCMonth()),
  };
};

const normalizeMonthLabel = (value) => {
  if (!value || typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const splitUtcTimeRange = (value) => {
  if (!value || typeof value !== "string") {
    return { startTimeUTC: "--:--", endTimeUTC: "--:--", timeRange: "-" };
  }

  const parts = value.split("-").map((item) => item.trim()).filter(Boolean);
  const start = (parts[0] || "--:--").replace(/\s*UTC\s*/i, "").trim();
  const end = (parts[1] || "--:--").replace(/\s*UTC\s*/i, "").trim();

  return {
    startTimeUTC: start,
    endTimeUTC: end,
    timeRange: `${start}-${end}`,
  };
};

const normalizeScheduleData = (payload) => {
  if (!payload) return EMPTY_RESPONSE;
  if (payload?.regions && Array.isArray(payload.regions)) {
    const data = payload.regions.map((regionItem) => {
      const plants = (regionItem?.plants || []).map((plant) => {
        const extractionTimes = splitUtcTimeRange(plant?.extractionTime);
        const publishTimes = splitUtcTimeRange(plant?.publishTime);

        return {
          plantId: plant?.plant || "-",
          extraction: {
            date: plant?.extractionDate || "-",
            timeRange: extractionTimes.timeRange,
            executed: Boolean(plant?.extractionExecuted),
          },
          publish: {
            date: plant?.publishDate || "-",
            timeRange: publishTimes.timeRange,
            executed: Boolean(plant?.publishExecuted),
          },
        };
      });

      const firstPlant = plants[0];
      const regionExtractionTimes = splitUtcTimeRange(regionItem?.plants?.[0]?.extractionTime);
      const regionPublishTimes = splitUtcTimeRange(regionItem?.plants?.[0]?.publishTime);

      return {
        region: regionItem?.region || "-",
        plantCount: regionItem?.totalPlants || plants.length,
        extractionSchedule: {
          date: firstPlant?.extraction?.date || "-",
          startTimeUTC: regionExtractionTimes.startTimeUTC,
          endTimeUTC: regionExtractionTimes.endTimeUTC,
        },
        publishSchedule: {
          date: firstPlant?.publish?.date || "-",
          startTimeUTC: regionPublishTimes.startTimeUTC,
          endTimeUTC: regionPublishTimes.endTimeUTC,
        },
        plants,
      };
    });

    const regionCounts = {};
    let totalPlants = 0;
    let fullyExecuted = 0;

    data.forEach((regionItem) => {
      regionCounts[regionItem.region] = regionItem.plants.length;
      totalPlants += regionItem.plants.length;
      regionItem.plants.forEach((plant) => {
        if (plant?.extraction?.executed && plant?.publish?.executed) {
          fullyExecuted += 1;
        }
      });
    });

    return {
      filters: {
        year: payload?.year || EMPTY_RESPONSE.filters.year,
        month: normalizeMonthLabel(payload?.month) || EMPTY_RESPONSE.filters.month,
        months: ALL_MONTHS,
      },
      summary: {
        totalPlants,
        fullyExecuted,
        regionCounts,
      },
      data,
    };
  }

  if (Array.isArray(payload)) {
    return {
      ...EMPTY_RESPONSE,
      data: payload,
    };
  }
  if (payload?.data && Array.isArray(payload.data)) {
    return {
      ...EMPTY_RESPONSE,
      ...payload,
      filters: {
        ...EMPTY_RESPONSE.filters,
        ...(payload.filters || {}),
      },
      summary: {
        ...EMPTY_RESPONSE.summary,
        ...(payload.summary || {}),
      },
      data: payload.data,
    };
  }
  return EMPTY_RESPONSE;
};

const normalizeRegionOptions = (payload) => {
  const extractRegionLabel = (item) => {
    if (typeof item === "string") return item.trim();
    if (item && typeof item === "object") {
      return String(item.region || item.regionName || item.value || "").trim();
    }
    return "";
  };

  const source = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.regions)
      ? payload.regions
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

  const unique = Array.from(
    new Set(
      source
        .map(extractRegionLabel)
        .filter(Boolean)
    )
  );

  return [DEFAULT_REGION_OPTION, ...unique];
};

const timeChipStyle = {
  borderRadius: "999px",
  px: 1,
  py: 0.2,
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  fontSize: 12,
  fontWeight: 600,
};

const RegionScheduleCard = ({ regionData, defaultExpanded = true }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const extractionRange = `${regionData?.extractionSchedule?.startTimeUTC || "--:--"}-${regionData?.extractionSchedule?.endTimeUTC || "--:--"}`;
  const publishRange = `${regionData?.publishSchedule?.startTimeUTC || "--:--"}-${regionData?.publishSchedule?.endTimeUTC || "--:--"}`;

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #d9d9d9",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(90deg, #f8fafc 0%, #ffffff 100%)",
          borderBottom: "1px solid #eceff1",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography fontSize={16} fontWeight={700}>
            {regionData.region}
          </Typography>
          <Chip
            size="small"
            label={`${regionData.plants?.length || regionData.plantCount || 0} plants`}
            sx={{ backgroundColor: "#e3f2fd", color: "#0d47a1", fontWeight: 600 }}
          />
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarMonthIcon sx={{ color: "#fb8c00", fontSize: 18 }} />
            <Typography fontSize={12} color="#616161">
              Extraction: {regionData?.extractionSchedule?.date || "N/A"}
            </Typography>
            <Box sx={{ ...timeChipStyle, backgroundColor: "#FFF3E0", color: "#E65100" }}>
              <AccessTimeIcon sx={{ fontSize: 14 }} /> {extractionRange} UTC
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <CalendarMonthIcon sx={{ color: "#1e88e5", fontSize: 18 }} />
            <Typography fontSize={12} color="#616161">
              Publish: {regionData?.publishSchedule?.date || "N/A"}
            </Typography>
            <Box sx={{ ...timeChipStyle, backgroundColor: "#E3F2FD", color: "#1565C0" }}>
              <AccessTimeIcon sx={{ fontSize: 14 }} /> {publishRange} UTC
            </Box>
          </Stack>
          <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Stack>
      </Box>

      {expanded && (
        <TableContainer sx={{ maxHeight: 360, overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Region</TableCell>
                <TableCell>Plant</TableCell>
                <TableCell>Extraction Date</TableCell>
                <TableCell>Extraction Time</TableCell>
                <TableCell align="center">Extraction Executed</TableCell>
                <TableCell>Publish Date</TableCell>
                <TableCell>Publish Time</TableCell>
                <TableCell align="center">Publish Executed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(regionData.plants || []).map((plant) => {
                const extractionDone = Boolean(plant?.extraction?.executed);
                const publishDone = Boolean(plant?.publish?.executed);
                return (
                  <TableRow key={`${regionData.region}_${plant.plantId}`} hover>
                    <TableCell>{regionData.region}</TableCell>
                    <TableCell>{plant.plantId}</TableCell>
                    <TableCell>{plant?.extraction?.date || "-"}</TableCell>
                    <TableCell>
                      <Box sx={{ ...timeChipStyle, backgroundColor: "#FFF8E1", color: "#F57F17" }}>
                        <AccessTimeIcon sx={{ fontSize: 14 }} /> {plant?.extraction?.timeRange || "-"}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        {extractionDone ? (
                          <CheckCircleIcon sx={{ color: "#2E7D32", fontSize: 20 }} />
                        ) : (
                          <CancelIcon sx={{ color: "#D32F2F", fontSize: 20 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{plant?.publish?.date || "-"}</TableCell>
                    <TableCell>
                      <Box sx={{ ...timeChipStyle, backgroundColor: "#E3F2FD", color: "#1565C0" }}>
                        <AccessTimeIcon sx={{ fontSize: 14 }} /> {plant?.publish?.timeRange || "-"}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        {publishDone ? (
                          <CheckCircleIcon sx={{ color: "#2E7D32", fontSize: 20 }} />
                        ) : (
                          <CancelIcon sx={{ color: "#D32F2F", fontSize: 20 }} />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

const ForecastExtractionScheduleTab = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [schedulePayload, setSchedulePayload] = useState(EMPTY_RESPONSE);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(monthName(new Date().getMonth()));
  const [selectedRegions, setSelectedRegions] = useState([DEFAULT_REGION_OPTION]);
  const [regionOptions, setRegionOptions] = useState([DEFAULT_REGION_OPTION]);

  const userEmail = localStorage.getItem("userEmail") || "";
  const userRole = localStorage.getItem("userRole") || "";
  const isSupplierRole = userRole === "Supplier Planner" || userRole === "Supplier Admin";

  useEffect(() => {
    const fetchAccessibleRegions = async () => {
      if (isSupplierRole) {
        setRegionOptions([DEFAULT_REGION_OPTION]);
        setSelectedRegions([DEFAULT_REGION_OPTION]);
        return;
      }

      if (!userEmail) {
        setRegionOptions([DEFAULT_REGION_OPTION]);
        return;
      }

      try {
        const res = await dispatch(
          getForecastHistoryFilterData({
            dynamicEndpoint: getBuyerExtractionCalendarAccessibleRegionsEndpoint,
            params: {
              email: userEmail,
            },
            skipUserEmailParam: true,
          })
        );

        if (res?.meta?.requestStatus === "fulfilled") {
          setRegionOptions(normalizeRegionOptions(res.payload));
        }
      } catch (error) {
        console.error("Error fetching accessible regions", error);
      }
    };

    fetchAccessibleRegions();
  }, [dispatch, isSupplierRole, userEmail]);

  useEffect(() => {
    if (!regionOptions.length) {
      setSelectedRegions([]);
      return;
    }

    setSelectedRegions((prev) => {
      if (!Array.isArray(prev)) return [DEFAULT_REGION_OPTION];
      if (prev.includes(DEFAULT_REGION_OPTION)) return [DEFAULT_REGION_OPTION];

      const validSelections = prev.filter((region) => regionOptions.includes(region));
      return validSelections;
    });
  }, [regionOptions]);

  useEffect(() => {
    const fetchSchedule = async () => {
      if (!userEmail) {
        setSchedulePayload(EMPTY_RESPONSE);
        return;
      }

      setLoading(true);
      try {
        const requestPayload = {
          year: selectedYear,
          month: String(selectedMonth).toUpperCase(),
          email: userEmail,
        };

        const res = await dispatch(
          getForecastHistoryFilterData({
            dynamicEndpoint: postBuyerExtractionCalendarEndpoint,
            data: requestPayload,
            requestMethod: "POST",
            skipUserEmailParam: true,
          })
        );
        if (res?.meta?.requestStatus === "fulfilled") {
          const normalized = normalizeScheduleData(res.payload);
          setSchedulePayload(normalized);
          const responseMonth = normalizeMonthLabel(normalized?.filters?.month);
          if (responseMonth && responseMonth !== selectedMonth && ALL_MONTHS.includes(responseMonth)) {
            setSelectedMonth(responseMonth);
          }
        }
      } catch (error) {
        console.error("Error fetching extraction schedule", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSchedule();
  }, [dispatch, selectedYear, selectedMonth, userEmail]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: YEAR_RANGE_COUNT }, (_, index) => currentYear + index);
  }, []);

  const monthOptions = useMemo(() => {
    const payloadMonths = schedulePayload?.filters?.months;
    if (Array.isArray(payloadMonths) && payloadMonths.length) {
      return payloadMonths;
    }
    return ALL_MONTHS;
  }, [schedulePayload]);

  useEffect(() => {
    if (yearOptions.length && !yearOptions.includes(selectedYear)) {
      setSelectedYear(yearOptions[0]);
    }
  }, [yearOptions, selectedYear]);

  useEffect(() => {
    if (monthOptions.length && !monthOptions.includes(selectedMonth)) {
      setSelectedMonth(monthOptions[0]);
    }
  }, [monthOptions, selectedMonth]);

  const selectedRegionLabels = useMemo(
    () => (Array.isArray(selectedRegions) ? selectedRegions : []),
    [selectedRegions]
  );

  const actualRegionOptions = useMemo(
    () => regionOptions.filter((region) => region !== DEFAULT_REGION_OPTION),
    [regionOptions]
  );

  const hasAllRegionsSelected = useMemo(
    () => selectedRegionLabels.includes(DEFAULT_REGION_OPTION),
    [selectedRegionLabels]
  );

  const normalizedSelectedRegions = useMemo(
    () => {
      const source = hasAllRegionsSelected ? actualRegionOptions : selectedRegionLabels;
      return source.map(normalizeRegionLabel).filter(Boolean);
    },
    [hasAllRegionsSelected, actualRegionOptions, selectedRegionLabels]
  );

  const selectedRegionsDisplay = useMemo(() => {
    if (hasAllRegionsSelected) return DEFAULT_REGION_OPTION;
    if (!selectedRegionLabels.length) return "NONE";
    return selectedRegionLabels.join(", ");
  }, [hasAllRegionsSelected, selectedRegionLabels]);

  const handleRegionChange = (event, child) => {
    const value = event.target.value;
    const nextSelectedRegions = typeof value === "string" ? value.split(",") : value;
    const toggledValue = child?.props?.value;

    if (toggledValue === DEFAULT_REGION_OPTION) {
      if (hasAllRegionsSelected) {
        setSelectedRegions([]);
      } else {
        setSelectedRegions([DEFAULT_REGION_OPTION]);
      }
      return;
    }

    const onlySpecificRegions = nextSelectedRegions.filter((region) => region !== DEFAULT_REGION_OPTION);
    if (!onlySpecificRegions.length) {
      setSelectedRegions([]);
      return;
    }

    const uniqueSpecificRegions = Array.from(new Set(onlySpecificRegions));
    if (uniqueSpecificRegions.length === actualRegionOptions.length) {
      setSelectedRegions([DEFAULT_REGION_OPTION]);
      return;
    }

    setSelectedRegions(uniqueSpecificRegions);
  };

  const filteredData = useMemo(() => {
    return (schedulePayload?.data || [])
      .map((regionItem) => ({
        ...regionItem,
        plants: (regionItem?.plants || []).filter((plant) => {
          const extraction = parseYearMonthFromDate(plant?.extraction?.date);
          const publish = parseYearMonthFromDate(plant?.publish?.date);
          const yearMatch = extraction.year === selectedYear || publish.year === selectedYear;
          const monthMatch = extraction.month === selectedMonth || publish.month === selectedMonth;
          return yearMatch && monthMatch;
        }),
      }))
      .filter((item) => {
        const hasPlants = (item?.plants || []).length > 0;
        if (!hasPlants) return false;
        if (hasAllRegionsSelected) return true;
        if (!normalizedSelectedRegions.length) return false;
        return normalizedSelectedRegions.includes(normalizeRegionLabel(item?.region));
      });
  }, [schedulePayload, selectedYear, selectedMonth, hasAllRegionsSelected, normalizedSelectedRegions]);

  const summary = useMemo(() => {
    const regionCounts = {};
    let totalPlants = 0;
    let fullyExecuted = 0;

    filteredData.forEach((regionItem) => {
      const plants = regionItem?.plants || [];
      regionCounts[regionItem.region] = plants.length;
      totalPlants += plants.length;
      plants.forEach((plant) => {
        if (plant?.extraction?.executed && plant?.publish?.executed) {
          fullyExecuted += 1;
        }
      });
    });

    return { totalPlants, fullyExecuted, regionCounts };
  }, [filteredData]);

  return (
    <Box sx={{ pb: 4 }}>
      <Box
        sx={{
          border: "1px solid #d9d9d9",
          borderRadius: 2,
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          background: "linear-gradient(135deg, #ffffff 0%, #f9fbff 100%)",
        }}
      >
        <Stack spacing={0.4}>
          <Typography fontSize={18} fontWeight={700}>
            Forecast Extraction Calendar
          </Typography>
          <Typography fontSize={13} color="#546e7a">
            Forecast extraction & publish dates across all regions and plants
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.2} sx={{ flexWrap: "wrap" }}>
          {!isSupplierRole && (
            <Stack spacing={0.4}>
              <Typography fontSize={12} color="#455a64" fontWeight={600}>
                Region
              </Typography>
              <Select
                size="small"
                multiple
                value={selectedRegionLabels}
                onChange={handleRegionChange}
                renderValue={(selected) => {
                  const selectedValues = Array.isArray(selected) ? selected : [];
                  if (selectedValues.includes(DEFAULT_REGION_OPTION)) {
                    return DEFAULT_REGION_OPTION;
                  }
                  if (!selectedValues.length) return "NONE";
                  return selectedValues.join(", ");
                }}
                sx={{ minWidth: 130, height: 36 }}
              >
                {regionOptions.map((region) => (
                  <MenuItem key={region} value={region}>
                    <Checkbox
                      size="small"
                      checked={
                        region === DEFAULT_REGION_OPTION
                          ? hasAllRegionsSelected
                          : hasAllRegionsSelected || selectedRegionLabels.includes(region)
                      }
                    />
                    <ListItemText primary={region} />
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          )}
          <Stack spacing={0.4}>
            <Typography fontSize={12} color="#455a64" fontWeight={600}>
              Year
            </Typography>
            <Select
              size="small"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ minWidth: 100, height: 36 }}
            >
              {yearOptions.map((year) => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </Stack>
          <Stack spacing={0.4}>
            <Typography fontSize={12} color="#455a64" fontWeight={600}>
              Month
            </Typography>
            <Select
              size="small"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              sx={{ minWidth: 130, height: 36 }}
            >
              {monthOptions.map((month) => (
                <MenuItem key={month} value={month}>{month}</MenuItem>
              ))}
            </Select>
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ border: "1px solid #d9d9d9", borderRadius: 2, p: 2 }}>
            <Typography fontSize={13} color="#607d8b" mb={0.4}>
              Viewing
            </Typography>
            <Typography fontSize={20} fontWeight={700}>
              {selectedMonth} {selectedYear}
            </Typography>
            <Typography fontSize={13} color="#607d8b" mt={0.3}>
              Region: {selectedRegionsDisplay}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ border: "1px solid #d9d9d9", borderRadius: 2, p: 2 }}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mb: 1 }}>
              {Object.entries(summary.regionCounts).map(([region, count]) => {
                const colors = {
                  APAC: { bg: "#E8F5E9", fg: "#1B5E20" },
                  EMEA: { bg: "#E3F2FD", fg: "#0D47A1" },
                  AMERICS: { bg: "#FFF3E0", fg: "#E65100" },
                  AMERICAS: { bg: "#FFF3E0", fg: "#E65100" },
                };
                const color = colors[region] || { bg: "#ECEFF1", fg: "#37474F" };
                return (
                  <Chip
                    key={region}
                    label={`${region} ${count}`}
                    sx={{ backgroundColor: color.bg, color: color.fg, fontWeight: 700 }}
                  />
                );
              })}
              <Chip
                label={`TOTAL ${summary.totalPlants}`}
                sx={{ backgroundColor: "#263238", color: "#fff", fontWeight: 700 }}
              />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <CheckCircleIcon sx={{ color: "#2E7D32", fontSize: 18 }} />
              <Typography fontSize={13} color="#455a64">
                Fully Executed: <strong>{summary.fullyExecuted}</strong> / {summary.totalPlants}
              </Typography>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Loader />
          </Box>
        ) : (
          <Stack spacing={2}>
            {filteredData.map((regionItem) => (
              <RegionScheduleCard
                key={regionItem.region}
                regionData={regionItem}
              />
            ))}
            {!filteredData.length && (
              <Paper elevation={0} sx={{ border: "1px solid #d9d9d9", borderRadius: 2, p: 3 }}>
                <Typography fontSize={14} color="#546e7a">
                  No schedule records found for selected filters.
                </Typography>
              </Paper>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default ForecastExtractionScheduleTab;